/**
 * AiDraftCard — Story 4-3a: Displays an AI-generated knowledge-check draft for review.
 *
 * Display only — all state mutations are delegated to the parent via callbacks.
 * No question is committed without explicit human action.
 *
 * @NonPhiEndpoint — draft content is clinical corpus, not member PHI.
 */

import type { JSX } from 'react';

import type { AiCheckDraftData } from './knowledge-check-panel.js';

// ── Types ──────────────────────────────────────────────────────────────────────

interface AiDraftCardProps {
  draft: AiCheckDraftData;
  onAcceptQuestion: (questionIndex: number) => void;
  onEditQuestion: (questionIndex: number) => void;
  onRejectQuestion: (questionIndex: number) => void;
}

// ── Component ──────────────────────────────────────────────────────────────────

export function AiDraftCard({
  draft,
  onAcceptQuestion,
  onEditQuestion,
  onRejectQuestion,
}: AiDraftCardProps): JSX.Element {
  return (
    <div
      data-testid="ai-draft-card"
      className="space-y-4 rounded-md border border-blue-200 bg-blue-50 p-4"
    >
      {/* Header */}
      <div className="flex items-center gap-3">
        <h3 className="text-base font-semibold text-gray-900">AI Draft (Review Required)</h3>
        <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
          AI-DRAFTED
        </span>
      </div>

      {/* Check-worthy suggestion */}
      {draft.checkWorthySuggestion && (
        <p data-testid="check-worthy-suggestion" className="text-sm text-blue-700">
          AI suggests this lesson may benefit from a knowledge check
        </p>
      )}

      {/* Spanish draft warning */}
      {draft.language === 'es' && (
        <div
          data-testid="spanish-translator-banner"
          className="rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-800"
        >
          This draft requires certified medical translator sign-off before publishing
        </div>
      )}

      {/* Draft questions */}
      <div className="space-y-4">
        {draft.questions.map((q, qIndex) => (
          <div
            key={q.questionId}
            className="space-y-2 rounded-md border border-blue-100 bg-white p-4"
          >
            <p className="text-sm font-medium text-gray-800">
              {q.stem || `Question ${qIndex + 1}`}
            </p>

            {/* Options preview */}
            <ul className="space-y-1 pl-4">
              {q.options.map((opt) => (
                <li
                  key={opt.optionId}
                  className={`text-sm ${opt.optionId === q.correctOptionId ? 'font-semibold text-green-700' : 'text-gray-600'}`}
                >
                  {opt.optionId === q.correctOptionId ? '✓ ' : ''}
                  {opt.text || '(empty)'}
                </li>
              ))}
            </ul>

            {/* Reinforcement copy */}
            {q.reinforcementCopy && (
              <p className="text-xs text-gray-500 italic">{q.reinforcementCopy}</p>
            )}

            {/* Action row */}
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                data-testid="accept-draft-btn"
                onClick={() => onAcceptQuestion(qIndex)}
                className="rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700"
              >
                Accept
              </button>
              <button
                type="button"
                data-testid={`edit-draft-btn-${qIndex}`}
                onClick={() => onEditQuestion(qIndex)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                Edit
              </button>
              <button
                type="button"
                data-testid="reject-draft-btn"
                onClick={() => onRejectQuestion(qIndex)}
                className="rounded-md border border-red-300 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50"
              >
                Reject
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
